import React, { useCallback, useEffect, useState } from "react";

import * as xlsx from "xlsx";

import { generateCropSheet, getAllCrops, getAllFarms, getCropsDataByYear, updateCropsBauData, updateCropsYearlyData } from "services";

import { changeLanguage, saveFile, useCancelAsyncOnUnmount, useTranslation } from "utils";

import { LANGUAGE_CODE } from "consts";

import { AdminHead, AdminSection, Button, Col, Form, MainPage, Option, Row, Select, TagNormal, Upload, Message as message } from "components";

export const Templates = () => {
  const [loading, setLoading] = useState(false);
  const [farms, setFarms] = useState([]);
  const [farm, setFarm] = useState(null);
  const [crops, setCrops] = useState([]);
  const [language, setLanguage] = useState(LANGUAGE_CODE.GERMAN);

  const { t } = useTranslation(["sheets"]);

  const signal = useCancelAsyncOnUnmount()();

  const loadAllCrops = useCallback(() => {
    getAllCrops({
      signal,
    }).then((res) => {
      setCrops(res);
    });
  }, [signal]);

  const loadAllFarms = useCallback(() => {
    getAllFarms({
      signal,
    })
      .then((res) => {
        setFarms(res.farms.length > 0 ? res.farms : []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  const loadFieldData = async ({ fieldId, year }) =>
    getCropsDataByYear({
      fieldId: fieldId,
      year: year,
      signal,
    }).then((res) => res);

  const generateSheet = () => {
    setLoading(true);
    generateCropSheet({
      signal,
      farmId: farm,
      language,
    })
      .then((res) => {
        saveFile(res, `Klim_${farm}.xlsx`);
        message({
          content: "The gremlins may have vomited something useful.",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const massageData = async (data) => {
    const massagedData = [];

    // want to see the raw excel data? this is the droid you're looking for
    // console.log("input", data);

    const farmId = Object.keys(data[0])[0];
    const lang = Object.values(data[0])[0];
    const years = Object.values(data[2]).filter((v) => v && typeof v === "number" && v >= 2020);

    changeLanguage(lang);

    let field = {};
    let mainCrop = null;
    let sideCrop = null;

    for (let i = 3; i < data.length; i += 1) {
      const currentRow = data[i];

      // if this row has the farm id, it's the first row of the field group
      // but also exclude some data, since we're hiding data in this column that would trigger the rule
      if (farmId in currentRow && currentRow[farmId] !== "" && !currentRow[farmId].includes(",")) {
        // construct a new field object
        field = {
          id: null,
          farmId,
          fieldId: currentRow[farmId],
          fieldModuleId: null, // needed
          moduleId: null, // needed
          bauEntries: {
            crops: {},
          },
          yearlyEntries: {
            crops: {},
          },
        };

        // get module details for this field, if exists
        if (!field.moduleId) {
          const yearList = data[i + 1][farmId];
          if (yearList && yearList.includes(",")) {
            // eslint-disable-next-line
            const fieldData = await loadFieldData({
              fieldId: field.fieldId,
              year: yearList.split(",")[1],
            });

            if (fieldData[0]) {
              field.id = fieldData[0].id;
              field.moduleId = fieldData[0].moduleId;
              field.fieldModuleId = fieldData[0].fieldModuleId;
            }
          }
        }

        // now we loop through the years
        for (let j = 0; j < 6; j += 1) {
          mainCrop = null;
          sideCrop = null;

          // if this row is the main crop (row 0 of group)
          // and an entry is in the year column
          // for residues, we must reverse engineer the text in the dropdown and map it to the corresponding language value for yes/no
          if (
            data[i + 0][`__EMPTY_${2 + j}`] &&
            data[i + 0][`__EMPTY_${2 + j}`] !== "" &&
            crops.find((crop) => crop.name === data[i + 0][`__EMPTY_${2 + j}`])
          ) {
            mainCrop = {
              cropId: crops.find((crop) => crop.name === data[i + 0][`__EMPTY_${2 + j}`]).id,
              residue: data[i + 1][`__EMPTY_${2 + j}`] === t("crops.input.yes"),
              mainCrop: true,
              yield: null,
            };
          }

          // if this row is the side crop (row 2 of group)
          // and an entry is in the year column
          if (
            data[i + 2][`__EMPTY_${2 + j}`] &&
            data[i + 2][`__EMPTY_${2 + j}`] !== "" &&
            crops.find((crop) => crop.name === data[i + 2][`__EMPTY_${2 + j}`])
          ) {
            sideCrop = {
              cropId: crops.find((crop) => crop.name === data[i + 2][`__EMPTY_${2 + j}`]).id,
              residue: data[i + 3][`__EMPTY_${2 + j}`] === t("crops.input.yes"),
              mainCrop: false,
              yield: null,
            };
          }

          // if main crop exists
          // and is the first 3 years
          // push to bau entries
          if (mainCrop && j < 3) {
            field.bauEntries.crops[years[j]] = [mainCrop];

            // if side crop also exists, push into same array
            if (sideCrop) {
              field.bauEntries.crops[years[j]].push(sideCrop);
            }
          }

          // otherwise if main crop exists, push to yearly entries
          if (mainCrop && j >= 3) {
            field.yearlyEntries.crops[years[j]] = [mainCrop];

            // if side crop also exists, push into same array
            if (sideCrop) {
              field.yearlyEntries.crops[years[j]].push(sideCrop);
            }
          }
        }

        massagedData.push(field);
      }
    }

    // want to see the resulting data? this is the droid you're looking for
    // console.log("massaged", massagedData);

    massagedData.forEach(async (item, i) => {
      // bau data must exist, otherwise no bueno
      if (Object.keys(item.bauEntries.crops).length > 0) {
        // eslint-disable-next-line
        await updateCropsBauData({
          signal,
          ...item,
        });

        // bau data must be complete to import yearly data
        if (Object.keys(item.yearlyEntries.crops).length > 0 && Object.keys(item.bauEntries.crops).length >= 3) {
          for (const [key, value] of Object.entries(item.yearlyEntries.crops)) {
            // eslint-disable-next-line
            await updateCropsYearlyData({
              signal,
              ...{
                year: key,
                fieldModuleId: item.fieldModuleId,
                farmId: item.farmId,
                fieldId: item.fieldId,
                moduleId: item.moduleId,
                yearlyData: {
                  crops: {
                    [key]: value,
                  },
                },
              },
            });
          }
        } else if (Object.keys(item.yearlyEntries.crops).length > 0) {
          message({
            content: "BAU data must be complete to import yearly data. Some fields were skipped.",
            icon: "gremlin",
          });
        }
      } else if (Object.keys(item.yearlyEntries.crops).length > 0) {
        message({
          content: "BAU data must be complete to import yearly data. Some fields were skipped.",
          icon: "gremlin",
        });
      }

      // give summary when done
      if (i === massagedData.length - 1) {
        message({
          content: "The gremlins have eaten your data.",
          icon: "gremlin",
        });

        setLoading(false);
      }
    });
  };

  const fileUpload = (file) => {
    setLoading(true);

    const isValidType = file.name.includes(".xls");

    if (!isValidType) {
      message({
        content: "Gremlins only eat xls or xlsx files.",
        icon: "gremlin",
      });
    } else if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);

        massageData(json);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  useEffect(() => {
    loadAllFarms();
    loadAllCrops();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainPage pageName={"admin"}>
      <AdminHead
        headline={
          <>
            Crop templates <TagNormal text="ALPHA" style={{ transform: "translate(0, -50%)", fontWeight: "bold" }} />
          </>
        }
        description="This experiment is for functionality to test data imports/exports via spreadsheets."
      />
      <AdminSection
        headline="Export a sheet"
        description={
          <>
            Select a farm to generate a sheet for.{" "}
            <span className="bold red-text">Large farms may generate large sheets - please be very patient when exporting/importing.</span>
          </>
        }
      />
      <Form>
        <Row className="margin-bottom-200">
          <Col md={6}>
            <Select
              name="farm"
              label="Select farm"
              rules={[{ required: true }]}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              onChange={(v) => setFarm(v)}
              optionLabelProp={"label"}
            >
              {farms.map((farm, i) => (
                <Option value={farm.id} key={i} label={farm.name}>
                  {farm.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col md={6}>
            <Select
              name="language"
              label="Select language"
              rules={[{ required: true }]}
              initialValue="IsiZulu"
              style={{ width: "100%" }}
              optionFilterProp="children"
              onChange={(v) => setLanguage(v)}
              optionLabelProp={"label"}
            >
              <Option value={LANGUAGE_CODE.GERMAN}>IsiJalimane</Option>
              <Option value={LANGUAGE_CODE.ENGLISH}>IsiNgisi</Option>
              <Option value={LANGUAGE_CODE.ZULU}>IsiZulu</Option>
            </Select>
          </Col>
          <Col md={8}>
            {farm && (
              <Button onClick={generateSheet} loading={loading} disabled={loading} type="primary">
                Download sheet
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <AdminSection
        headline="Import a sheet"
        description={
          <>
            Select a file to import. All existing data will be modified - any incorrect data will result in failure.
            <br />
            <br />
            <b>
              Rows and columns are identified by number - moved/removed/changed rows and columns will result in failure. Crops are matched by name - if any
              crops have the same name or are not found, failure may ensue. Yield data is zero, and will overwrite existing yield data.
            </b>
          </>
        }
        extra={
          <Upload action="" beforeUpload={(v) => fileUpload(v)} name="file" showUploadList={false} className="auto-height auto-width">
            <Button type="primary" size="small" disabled={loading} loading={loading}>
              Upload file
            </Button>
          </Upload>
        }
      />
    </MainPage>
  );
};
